import { TFnType } from '@tolgee/react'
import { EmptyIndicator } from '@tom-ui/ui'
import { MAX_INT32_VALUE } from '@tom-ui/utils'
import {
  AllocationDestinationDto,
  AllocationRuleDto,
  RuleFacets,
  CarrierVisitDirection,
  UpsertAllocationRuleRequest,
  UpsertTemplateAllocationRuleRequest,
  YardBlockDto,
  EnforcementMode,
} from '@yard/app/api'
import { YardPositionBlockRange } from '@yard/components/YardPositionSelector/models/yard-position-block-range.model'
import { generateAllocationRuleId, isNewAllocationRule } from '@yard/utils/allocation-rules.util'
import {
  BooleanSelectOption,
  convertBooleanSelectOptionToBoolean,
  convertBooleanToBooleanSelectOption,
} from '@yard/utils/boolean-select.util'
import { AllocationRuleFormData } from '../models/allocation-rule.model'

export const mapAllocationRuleDtoToUpsertAllocationRuleRequest = (
  allocationRule: AllocationRuleDto,
): UpsertAllocationRuleRequest => {
  return {
    id: isNewAllocationRule(allocationRule.id) ? undefined : allocationRule.id,
    allocationRule: {
      name: allocationRule.name,
      priority: allocationRule.priority,
      enforcementMode: allocationRule.enforcementMode ?? null,
      facets: {
        ...allocationRule.facets,
        direction: allocationRule.facets.direction ?? CarrierVisitDirection.Outbound,
      },
      destinations: allocationRule.destinations,
    },
  }
}

export const mapFormDataToAllocationRuleTemplateUpsertRequest = (
  formData: AllocationRuleFormData,
  id?: string,
): UpsertTemplateAllocationRuleRequest => {
  return {
    id,
    allocationRule: mapFormDataToAllocationRule(formData, id, undefined, true),
  }
}

export const mapFormDataToAllocationRule = (
  formData: AllocationRuleFormData,
  id?: string,
  priority?: number,
  isTemplate = false,
): AllocationRuleDto => {
  const isDangerousYes = formData.facets.isDangerous === BooleanSelectOption.True
  return {
    id: id ?? generateAllocationRuleId(),
    name: formData.name,
    isTemplate,
    priority: priority ?? 1,
    enforcementMode: isDangerousYes ? formData.facets.enforcementMode : null,
    facets: {
      size: formData.facets.size,
      isEmpty: convertBooleanSelectOptionToBoolean(formData.facets.isEmpty),
      isReefer: convertBooleanSelectOptionToBoolean(formData.facets.isReefer),
      isDangerous: convertBooleanSelectOptionToBoolean(formData.facets.isDangerous),
      isOOG: convertBooleanSelectOptionToBoolean(formData.facets.isOOG),
      isRestow: convertBooleanSelectOptionToBoolean(formData.facets.isRestow),
      containerOperatorId: formData.facets.containerOperatorId,
      customerId: formData.facets.customerId,
      consigneeId: formData.facets.consigneeId,
      portOfDischarge: formData.facets.portOfDischarge,
      containerNumber: formData.facets.containerNumber,
      containerHeight: formData.facets.containerHeight,
      containerType: formData.facets.containerType,
      carrierType: formData.facets.carrierType,
      carrierVisitId: formData.facets.carrierVisitId,
      direction: formData.facets.direction,
      imoClasses: isDangerousYes ? (formData.facets.imoClasses ?? []) : null,
    },
    destinations: formData.destinations.map(dest => ({
      blockId: dest.blockId,
      minBay: dest.minBay,
      maxBay: dest.maxBay,
      minRow: dest.minRow,
      maxRow: dest.maxRow,
      priority: dest.position,
    })),
  }
}

export const mapYardPositionDestinationRangeToAllocationDestinationDto = (
  position: YardPositionBlockRange,
  priority = 0,
): AllocationDestinationDto => {
  return {
    blockId: position.blockId,
    minBay: position.minBay,
    maxBay: position.maxBay,
    minRow: position.minRow,
    maxRow: position.maxRow,
    priority: priority,
  }
}

export const mapAllocationRuleDestinationToYardPositionDescriptor = (
  block?: YardBlockDto,
  minBay?: number,
  maxBay?: number,
  minRow?: number,
  maxRow?: number,
): string => {
  if (!block) return EmptyIndicator

  if (block.type === 'FlexibleBlock') return block.name

  const parts = []

  if (block.name) parts.push(block.name)
  const bay = convertMinMaxToRangeString(block.bayNames, minBay, maxBay)
  const row = convertMinMaxToRangeString(block.rowNames, minRow, maxRow)

  if (bay) parts.push(bay)
  if (row) parts.push(row)

  return parts.join('.')
}
const convertMinMaxToRangeString = (names: string[], min?: number, max?: number) => {
  if (min == null || max === MAX_INT32_VALUE) return undefined
  if (max == null || max === min) return names[min - 1]

  return `${names[min - 1]}-${names[max - 1]}`
}

const mapImoClassesToString = (imoClasses: string[] | null | undefined, t: TFnType): string => {
  if (!imoClasses || imoClasses.length === 0) return t('allDg', 'All DG')

  return [...imoClasses].sort((a, b) => a.localeCompare(b)).join(', ')
}

export const mapAllocationRuleFacetsToString = (
  facets: RuleFacets,
  t: TFnType,
  enforcementMode?: EnforcementMode | null,
): string => {
  const facetElements = [
    facets.size && `${facets.size}′`,
    facets.isEmpty != null && (facets.isEmpty ? t('empty', 'Empty') : t('full', 'Full')),
    facets.isOOG === true ? t('oog', 'OOG') : '',
    facets.isRestow === true ? t('restow', 'Restow') : '',
    facets.isReefer === true ? t('reefer', 'Reefer') : '',
    facets.isDangerous === true ? t('dangerous', 'Dangerous') : '',
    facets.isDangerous === true &&
      `${t('imoClasses', 'IMO classes')}: ${mapImoClassesToString(facets.imoClasses, t)}`,
    facets.isDangerous === true &&
      enforcementMode &&
      `${t('enforcementMode', 'Enforcement mode')}: ${enforcementMode}`,
    facets.containerHeight,
    facets.containerType,
    facets.carrierType && `${t('carrierType', 'Carrier type')}: ${facets.carrierType}`,
    facets.direction && `${t('direction', 'Direction')}: ${facets.direction}`,
    facets.portOfDischarge &&
      `${t('portOfDischarge', 'Port of discharge')}: ${facets.portOfDischarge}`,
    facets.isDangerous === false ? t('nonDangerous', 'Non Dangerous') : '',
    facets.isReefer === false ? t('nonReefer', 'Non Reefer') : '',
    facets.isOOG === false ? t('nonOOG', 'Non OOG') : '',
  ].filter(Boolean)

  return facetElements.join(', ')
}

export const mapAllocationRuleDtoToFormData = (rule: AllocationRuleDto): AllocationRuleFormData => {
  return {
    name: rule.name,
    facets: {
      size: rule.facets.size || undefined,
      isEmpty: convertBooleanToBooleanSelectOption(rule.facets.isEmpty),
      isReefer: convertBooleanToBooleanSelectOption(rule.facets.isReefer),
      isDangerous: convertBooleanToBooleanSelectOption(rule.facets.isDangerous),
      isOOG: convertBooleanToBooleanSelectOption(rule.facets.isOOG),
      isRestow: convertBooleanToBooleanSelectOption(rule.facets.isRestow),
      containerOperatorId: rule.facets.containerOperatorId || undefined,
      customerId: rule.facets.customerId || undefined,
      consigneeId: rule.facets.consigneeId || undefined,
      portOfDischarge: rule.facets.portOfDischarge || undefined,
      containerNumber: rule.facets.containerNumber || undefined,
      containerHeight: rule.facets.containerHeight || undefined,
      containerType: rule.facets.containerType || undefined,
      carrierType: rule.facets.carrierType ?? undefined,
      carrierVisitId: rule.facets.carrierVisitId ?? undefined,
      direction: rule.facets.direction ?? undefined,
      imoClasses: rule.facets.imoClasses ?? [],
      enforcementMode: rule.enforcementMode ?? null,
    },
    destinations: rule.destinations.map((dest: AllocationDestinationDto) => ({
      blockId: dest.blockId,
      minBay: dest.minBay,
      maxBay: dest.maxBay,
      minRow: dest.minRow,
      maxRow: dest.maxRow,
      position: dest.priority,
    })),
  }
}
